import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { VIEWS, VIEW_LABELS } from "./data/collections";
import { bagParams, track } from "./analytics";
import { CATEGORY_LABELS, pick, provinceName, useLang } from "./i18n";

// CSS 3D prism (MVP ตามสเปกข้อ 8) — ข้อจำกัด: ไม่มีความโค้ง/ตะเข็บ, หูหิ้วเป็นระนาบ
// ลำดับรอบถุงตามภาพกาง: front → side1 (ต่อขอบขวาของ front) → back → side2

const VIEW_ANGLES = {
  front: { yaw: 0, pitch: -10 },
  side1: { yaw: -90, pitch: -10 },
  back: { yaw: 180, pitch: -10 },
  side2: { yaw: 90, pitch: -10 },
  bottom: { yaw: 0, pitch: 75 },
};

// เวกเตอร์ตั้งฉากของแต่ละด้าน (แกน CSS: x ขวา, y ลง, z เข้าหาผู้ชม)
const NORMALS = {
  front: [0, 0, 1],
  back: [0, 0, -1],
  side1: [1, 0, 0],
  side2: [-1, 0, 0],
  bottom: [0, 1, 0],
};

// ซูมที่ตัวถุง (ปุ่ม / สองนิ้ว / pinch บน trackpad / ดับเบิลคลิก) — เกิน ZOOMED ถือว่ากำลังซูม: ลากเพื่อเลื่อนดู และแสดงจุดเด่นอัตโนมัติ
const SCALE_MIN = 0.7;
const SCALE_MAX = 3;
const SCALE_STEP = 0.25;
const ZOOMED = 1.05;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const PITCH_MIN = -35;
const PITCH_MAX = 80;
const RAD = Math.PI / 180;

// ค่า z ของ normal หลัง rotateX(pitch) rotateY(yaw) — > 0 แปลว่าด้านนั้นหันเข้าหาผู้ชม
const facing = ([x, y, z], yaw, pitch) => {
  const z1 = -x * Math.sin(yaw * RAD) + z * Math.cos(yaw * RAD);
  return y * Math.sin(pitch * RAD) + z1 * Math.cos(pitch * RAD);
};

const nearestYaw = (target, current) =>
  target + 360 * Math.round((current - target) / 360);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ค่าเปิด/ปิดจุดเด่นจำไว้ต่อเครื่อง — ค่าเริ่มต้นคือซ่อน (V2) เพื่อให้เห็นลายถุงเต็ม ๆ
// (browser storage อาจใช้ไม่ได้ใน private mode)
const HOTSPOT_PREF = "bce_show_hotspots_v2";
const readHotspotPref = () => {
  try {
    return window.localStorage.getItem(HOTSPOT_PREF) === "1";
  } catch (e) {
    return false;
  }
};

// คำแนะนำการหมุน: แสดงจนกว่าผู้ใช้จะหมุนถุงเองหรือกด "เข้าใจแล้ว" ครั้งแรก
const GUIDE_PREF = "bce_rotate_guide_seen";
const readGuideSeen = () => {
  try {
    return window.localStorage.getItem(GUIDE_PREF) === "1";
  } catch (e) {
    return false;
  }
};

export default function BagViewer({
  collection,
  selectedHotspotId,
  onHotspotSelect,
  onViewChange,
  viewRequest,
  autoRotate,
  onAutoRotateChange,
}) {
  const sceneRef = useRef(null);
  const dragRef = useRef(null);
  const rotatedRef = useRef(false); // bag_rotate นับครั้งแรกต่อถุง
  const [yaw, setYaw] = useState(0);
  const [pitch, setPitch] = useState(-10);
  // s = ขนาด, x/y = ระยะเลื่อน (px) ของถุงในพื้นที่แสดง
  const [zoom, setZoom] = useState({ s: 1, x: 0, y: 0 });
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const scale = zoom.s;
  const zoomed = scale > ZOOMED;
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const zoomTrackedRef = useRef(false); // bag_zoom นับครั้งแรกต่อถุง
  const [tipId, setTipId] = useState(null); // tooltip ของจุดเด่นบนถุง
  const [tipPos, setTipPos] = useState(null);
  const [settleTick, setSettleTick] = useState(0);
  const [animating, setAnimating] = useState(true);
  const [bagWidth, setBagWidth] = useState(200);
  const bagWidthRef = useRef(200);
  bagWidthRef.current = bagWidth;
  const [status, setStatus] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const reducedMotion = useMemo(prefersReducedMotion, []);
  const [showHotspots, setShowHotspots] = useState(readHotspotPref);
  const [dragging, setDragging] = useState(false);
  const [showGuide, setShowGuide] = useState(() => !readGuideSeen());
  const { lang, t } = useLang();

  const dismissGuide = useCallback(() => {
    setShowGuide(false);
    try {
      window.localStorage.setItem(GUIDE_PREF, "1");
    } catch (e) {
      // แสดงอีกครั้งในครั้งหน้าได้ ไม่เป็นไร
    }
  }, []);

  const toggleHotspots = () => {
    track("hotspot_toggle", { ...bagParams(collection), visible: !showHotspots });
    setShowHotspots((v) => {
      try {
        window.localStorage.setItem(HOTSPOT_PREF, v ? "0" : "1");
      } catch (e) {
        // ใช้ค่าในหน้าต่อไปได้ แม้บันทึกไม่ได้
      }
      return !v;
    });
  };

  // ขนาดถุงตามพื้นที่ scene (รวมความสูงหูหิ้ว ~1.45 เท่าตัวถุง)
  useEffect(() => {
    const el = sceneRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const w = Math.min(width * 0.5, (height - 70) / 2.05, 240);
      setBagWidth(Math.max(130, Math.round(w)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    rotatedRef.current = false;
    zoomTrackedRef.current = false;
    setTipId(null);
  }, [collection.id]);

  const trackZoom = (method) => {
    if (zoomTrackedRef.current) return;
    zoomTrackedRef.current = true;
    track("bag_zoom", { ...bagParams(collection), method });
  };

  // จำกัดระยะเลื่อนไม่ให้ถุงหลุดออกนอกพื้นที่แสดง
  const clampPan = (z, w, h) => {
    if (z.s <= 1.001) return { s: z.s, x: 0, y: 0 };
    const mx = w * z.s * 0.55;
    const my = h * z.s * 0.6;
    return { s: z.s, x: clamp(z.x, -mx, mx), y: clamp(z.y, -my, my) };
  };

  // ซูมไปที่ขนาด next (ค่า หรือฟังก์ชันของขนาดเดิม) โดยให้จุด (cx, cy) บนจออยู่ที่เดิม
  const zoomAt = (next, cx, cy) => {
    const el = sceneRef.current;
    setZoom((z) => {
      const s = clamp(typeof next === "function" ? next(z.s) : next, SCALE_MIN, SCALE_MAX);
      let { x, y } = z;
      if (el && cx != null && s > 1.001) {
        const r = el.getBoundingClientRect();
        const k = s / z.s;
        x = z.x - (cx - (r.left + r.width / 2 + z.x)) * (k - 1);
        y = z.y - (cy - (r.top + r.height / 2 + z.y)) * (k - 1);
      }
      return clampPan({ s, x, y }, bagWidthRef.current, bagWidthRef.current * 1.4);
    });
  };

  const trackRotate = (method) => {
    if (showGuide) dismissGuide();
    if (rotatedRef.current) return;
    rotatedRef.current = true;
    track("bag_rotate", { ...bagParams(collection), method });
  };

  // preload texture ทั้ง 5 ด้าน
  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    Promise.all(
      VIEWS.map(
        (view) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = collection.textures[view];
          })
      )
    )
      .then(() => !cancelled && setStatus("ready"))
      .catch(() => !cancelled && setStatus("error"));
    return () => {
      cancelled = true;
    };
  }, [collection, reloadKey]);

  // แนะนำว่าหมุนได้: ส่ายถุงซ้าย–ขวาเบา ๆ ครั้งเดียวเมื่อโหลดเสร็จและยังแสดงคำแนะนำ (ข้ามถ้าลดการเคลื่อนไหว)
  const wiggledRef = useRef(false);
  useEffect(() => {
    if (status !== "ready" || !showGuide || reducedMotion || wiggledRef.current) return undefined;
    wiggledRef.current = true;
    const steps = [
      [500, -35],
      [1300, 30],
      [2100, 0],
    ];
    const timers = steps.map(([ms, deg]) =>
      setTimeout(() => {
        if (dragRef.current) return; // ผู้ใช้เริ่มลากเองแล้ว
        setAnimating(true);
        setYaw(deg);
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [status, showGuide, reducedMotion]);

  const goTo = useCallback(
    (view) => {
      const target = VIEW_ANGLES[view];
      onAutoRotateChange(false);
      setAnimating(!reducedMotion);
      setYaw((y) => nearestYaw(target.yaw, y));
      setPitch(target.pitch);
    },
    [onAutoRotateChange, reducedMotion]
  );

  // คำสั่งเปลี่ยนมุมจากภายนอก (ปุ่ม view, เลือก landmark, เปลี่ยนจังหวัด)
  useEffect(() => {
    if (!viewRequest) return;
    if (viewRequest.resetZoom) setZoom({ s: 1, x: 0, y: 0 });
    goTo(viewRequest.view);
    setTipId(viewRequest.tipId || null); // "สำรวจจุดเด่น": หมุนไปด้านนั้นแล้วเปิด tooltip
  }, [viewRequest, goTo]);

  // auto rotate (opt-in) — ทิศเดียวกับการลากไปทางซ้าย: front → side1 → back → side2
  useEffect(() => {
    if (!autoRotate) return undefined;
    setAnimating(false);
    setPitch(-10);
    let raf;
    let last = performance.now();
    const tick = (now) => {
      setYaw((y) => y - (now - last) * 0.03);
      last = now;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoRotate]);

  const facings = useMemo(() => {
    const out = {};
    VIEWS.forEach((v) => {
      out[v] = facing(NORMALS[v], yaw, pitch);
    });
    return out;
  }, [yaw, pitch]);

  const activeView = useMemo(
    () => VIEWS.reduce((best, v) => (facings[v] > facings[best] ? v : best), "front"),
    [facings]
  );

  useEffect(() => {
    onViewChange(activeView);
  }, [activeView, onViewChange]);

  const onPointerDown = (e) => {
    if (e.target.closest("button")) return;
    setTipId(null);
    onAutoRotateChange(false);
    setAnimating(false);
    const pts = pointersRef.current;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);
    if (pts.size === 2) {
      // สองนิ้ว = ซูม (pinch) แทนการหมุน
      const [p1, p2] = [...pts.values()];
      pinchRef.current = { dist: Math.hypot(p1.x - p2.x, p1.y - p2.y), mid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 } };
      dragRef.current = null;
      return;
    }
    dragRef.current = { x: e.clientX, y: e.clientY, type: e.pointerType, mode: zoomRef.current.s > ZOOMED ? "pan" : "rotate" };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    const pts = pointersRef.current;
    if (!pts.has(e.pointerId)) return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const pinch = pinchRef.current;
    if (pinch && pts.size >= 2) {
      const [p1, p2] = [...pts.values()];
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      const k = dist / (pinch.dist || 1);
      zoomAt((s0) => s0 * k, mid.x, mid.y);
      setZoom((z) => clampPan({ ...z, x: z.x + mid.x - pinch.mid.x, y: z.y + mid.y - pinch.mid.y }, bagWidthRef.current, bagWidthRef.current * 1.4));
      pinchRef.current = { dist, mid };
      if (Math.abs(k - 1) > 0.01) trackZoom("pinch");
      return;
    }

    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    d.x = e.clientX;
    d.y = e.clientY;
    if (d.mode === "pan") {
      // กำลังซูม: ลากเพื่อเลื่อนดูรายละเอียด
      setZoom((z) => clampPan({ ...z, x: z.x + dx, y: z.y + dy }, bagWidthRef.current, bagWidthRef.current * 1.4));
      return;
    }
    if (Math.abs(dx) > 1) trackRotate(d.type === "touch" ? "swipe" : "drag");
    setYaw((y) => y + dx * 0.5);
    // บนจอสัมผัสให้ปัดแนวตั้งเลื่อนหน้าได้ตามปกติ (touch-action: pan-y) จึงปรับ pitch เฉพาะเมาส์/ปากกา
    if (d.type !== "touch") {
      setPitch((p) => Math.min(PITCH_MAX, Math.max(PITCH_MIN, p - dy * 0.35)));
    }
  };

  const endDrag = (e) => {
    const pts = pointersRef.current;
    pts.delete(e.pointerId);
    if (pts.size < 2) pinchRef.current = null;
    if (pts.size === 0) {
      dragRef.current = null;
      setDragging(false);
    }
  };

  // pinch บน trackpad / ctrl+scroll = ซูม (scroll ปกติยังเลื่อนหน้าได้) — ต้องเป็น non-passive จึง preventDefault ได้
  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      // จำกัดต่อครั้ง: ล้อเมาส์ 1 คลิก (~100) ซูมไม่เกิน ~1.6 เท่า, pinch บน trackpad (ค่าเล็ก) ยังซูมนุ่ม
      zoomAt((s0) => s0 * Math.exp(-clamp(e.deltaY, -50, 50) * 0.01), e.clientX, e.clientY);
      trackZoom("wheel");
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  const onDoubleClick = (e) => {
    if (e.target.closest("button")) return;
    setTipId(null);
    zoomAt(zoomRef.current.s > ZOOMED ? 1 : 2, e.clientX, e.clientY);
    trackZoom("dblclick");
  };

  const zoomBy = (delta) => {
    zoomAt((s0) => +(s0 + delta).toFixed(2));
    if (delta > 0) trackZoom("button");
  };

  const rotateBy = (deg, method = "arrow_button") => {
    trackRotate(method);
    onAutoRotateChange(false);
    setAnimating(!reducedMotion);
    setYaw((y) => y + deg);
    setPitch((p) => (p > 40 ? -10 : p));
  };

  const onSceneKeyDown = (e) => {
    if (e.key === "Escape" && tipId) {
      setTipId(null);
      return;
    }
    if (e.target !== e.currentTarget) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      rotateBy(45, "keyboard");
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      rotateBy(-45, "keyboard");
    }
  };

  const tipHotspot = tipId ? collection.hotspots.find((h) => h.id === tipId) : null;

  useLayoutEffect(() => {
    if (!tipId) {
      setTipPos(null);
      return;
    }
    const el = sceneRef.current;
    const btn = el && el.querySelector(`[data-hs="${tipId}"]`);
    if (!btn) return;
    const r = el.getBoundingClientRect();
    const b = btn.getBoundingClientRect();
    setTipPos({ x: b.left + b.width / 2 - r.left, y: b.top + b.height / 2 - r.top, w: r.width, h: r.height });
  }, [tipId, yaw, pitch, zoom, bagWidth, settleTick]);

  // ตำแหน่งระหว่าง transition การหมุนยังไม่นิ่ง — คำนวณซ้ำเมื่อหมุนเสร็จ
  useEffect(() => {
    if (!tipId) return undefined;
    const tm = setTimeout(() => setSettleTick((n) => n + 1), 760);
    return () => clearTimeout(tm);
  }, [tipId, yaw, pitch, zoom]);

  const W = bagWidth;
  const H = Math.round(W * collection.proportions.height);
  const D = Math.round(W * collection.proportions.depth);
  const handleH = H * Math.max(collection.handles.front.height, collection.handles.back.height);

  const faceStyle = (view, w, h, transform) => ({
    width: w,
    height: h,
    left: (W - w) / 2,
    top: (H - h) / 2,
    transform,
    backgroundImage:
      status === "ready" && collection.textures[view] ? `url("${collection.textures[view]}")` : undefined,
  });

  const faces = {
    front: faceStyle("front", W, H, `translateZ(${D / 2}px)`),
    back: faceStyle("back", W, H, `rotateY(180deg) translateZ(${D / 2}px)`),
    side1: faceStyle("side1", D, H, `rotateY(90deg) translateZ(${W / 2}px)`),
    side2: faceStyle("side2", D, H, `rotateY(-90deg) translateZ(${W / 2}px)`),
    bottom: faceStyle("bottom", W, D, `rotateX(-90deg) translateZ(${H / 2}px)`),
  };

  // หูหิ้ว 1 ห่วงต่อด้าน (หน้า/หลัง สีแยกกันได้) — ซ่อนเฉพาะห่วงที่อยู่ตรงหลังอีกห่วงพอดี เพื่อให้มุมหน้า/หลังเห็นห่วงเดียว
  const handleLoop = (side) => {
    const h = collection.handles[side];
    const strap = Math.max(6, Math.round(W * h.thickness));
    const loopH = H * h.height;
    const opacity = Math.min(1, Math.max(0, (facings[side] + 0.95) / 0.2));
    return (
      <div
        className="bce-handle"
        style={{
          width: W,
          height: H,
          opacity,
          transform: `${side === "back" ? "rotateY(180deg) " : ""}translateZ(${D / 2 + 1}px)`,
        }}
      >
        <div
          className="bce-handle-loop"
          style={{
            left: W * h.left,
            width: W * h.width,
            top: -loopH,
            height: loopH + 2,
            borderWidth: `${strap}px ${strap}px 0`,
            borderColor: h.color,
            borderTopLeftRadius: (W * h.width) / 2,
            borderTopRightRadius: (W * h.width) / 2,
          }}
        />
      </div>
    );
  };

  return (
    <div className="bce-viewer">
      <div
        ref={sceneRef}
        className="bce-scene"
        tabIndex={0}
        role="group"
        aria-roledescription="3D viewer"
        aria-label={t("viewerLabel", provinceName(collection, lang), VIEW_LABELS[activeView][lang])}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={onDoubleClick}
        onKeyDown={onSceneKeyDown}
        style={{ touchAction: zoomed ? "none" : "pan-y", cursor: zoomed ? "move" : undefined }}
      >
        <div className="bce-pedestal" aria-hidden="true" />
        {/* ปุ่มซูมมุมขวาบน แบบ Google Maps */}
        <div className="bce-zoom-tools" role="group" aria-label={t("zoomControls")}>
          <button type="button" aria-label={t("enlarge")} title={t("enlarge")} onClick={() => zoomBy(SCALE_STEP)} disabled={scale >= SCALE_MAX}>
            ＋
          </button>
          <button type="button" aria-label={t("shrink")} title={t("shrink")} onClick={() => zoomBy(-SCALE_STEP)} disabled={scale <= SCALE_MIN}>
            −
          </button>
          {Math.abs(scale - 1) > 0.01 && (
            <button type="button" className="bce-zoom-reset" aria-label={t("resetSize")} title={t("resetSize")} onClick={() => setZoom({ s: 1, x: 0, y: 0 })}>
              ⟲
            </button>
          )}
        </div>
        <button type="button" className="bce-dir bce-dir--prev" aria-label={t("rotateLeft")} onClick={() => rotateBy(45)}>
          ‹
        </button>

        <div className="bce-stage" style={{ width: W, height: H, transform: `translate(${zoom.x}px, ${zoom.y}px) translateY(${handleH * 0.42}px)` }}>
          <div
            className={`bce-bag${animating ? " is-animating" : ""}`}
            style={{ width: W, height: H, transform: `rotateX(${pitch}deg) rotateY(${yaw}deg) scale(${scale})` }}
          >
            {VIEWS.map((view) => {
              const facingViewer = facings[view] > 0.3;
              // ซ่อนจุดระหว่างลาก/หมุนอัตโนมัติ และเมื่อผู้ใช้ปิดจุดเด่น (ยกเว้นจุดที่เลือกอยู่) เพื่อให้เห็นลายถุงเต็ม ๆ
              return (
                <div key={view} className={`bce-face bce-face--${view}`} style={faces[view]}>
                  {status === "ready" &&
                    collection.hotspots
                      .filter((h) => h.view === view)
                      .map((h) => {
                        const visible =
                          facingViewer &&
                          !dragging &&
                          !autoRotate &&
                          (showHotspots || zoomed || tipId === h.id || selectedHotspotId === h.id);
                        return (
                        <button
                          key={h.id}
                          type="button"
                          data-hs={h.id}
                          className={`bce-hotspot${tipId === h.id ? " is-selected" : ""}${visible ? "" : " is-hidden"}${h.category ? ` bce-cat--${h.category}` : ""}`}
                          style={{ left: `${h.u * 100}%`, top: `${h.v * 100}%`, transform: `scale(${1 / scale})` }}
                          tabIndex={visible ? 0 : -1}
                          aria-hidden={!visible}
                          aria-label={t("hotspotLabel", pick(h, "title", lang))}
                          aria-expanded={tipId === h.id}
                          onClick={() => {
                            const open = tipId !== h.id;
                            setTipId(open ? h.id : null);
                            if (open) onHotspotSelect(h.id);
                          }}
                        >
                          <span aria-hidden="true">+</span>
                        </button>
                        );
                      })}
                </div>
              );
            })}
            <div className="bce-face bce-face--top" style={faceStyle("top", W, D, `rotateX(90deg) translateZ(${H / 2}px)`)} />
            {handleLoop("front")}
            {handleLoop("back")}
          </div>
        </div>

        <button type="button" className="bce-dir bce-dir--next" aria-label={t("rotateRight")} onClick={() => rotateBy(-45)}>
          ›
        </button>

        {tipHotspot && tipPos && !dragging && facings[tipHotspot.view] > 0.3 && (
          <div className="bce-bag-tip-anchor" style={{ left: tipPos.x, top: tipPos.y }}>
            <div
              className={`bce-zoom-tip ${tipPos.y < tipPos.h * 0.42 ? "below" : "above"} ${
                tipPos.x < tipPos.w * 0.35 ? "start" : tipPos.x > tipPos.w * 0.65 ? "end" : "mid"
              }`}
              role="tooltip"
              onPointerDown={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              {tipHotspot.category && (
                <span className={`bce-zoom-tip-cat bce-cat--${tipHotspot.category}`}>{CATEGORY_LABELS[tipHotspot.category][lang]}</span>
              )}
              <b>{pick(tipHotspot, "title", lang)}</b>
              <p>{pick(tipHotspot, "description", lang)}</p>
              <button type="button" className="bce-zoom-tip-close" aria-label={t("closeTip")} onClick={() => setTipId(null)}>
                ×
              </button>
            </div>
          </div>
        )}

        {status === "loading" && (
          <div className="bce-scene-status" role="status">
            <span className="bce-spinner" aria-hidden="true" />
            {t("loading")}
          </div>
        )}
        {status === "error" && (
          <div className="bce-scene-status" role="alert">
            {t("loadError")}
            <button type="button" className="bce-pill" onClick={() => setReloadKey((k) => k + 1)}>
              {t("retry")}
            </button>
          </div>
        )}

        {showGuide && status === "ready" && (
          <>
            <div className="bce-guide-hand" aria-hidden="true">
              <span>☝</span>
            </div>
            <div className="bce-guide" role="note">
              <b>{t("guideTitle")}</b>
              <ul>
                <li>
                  <span aria-hidden="true">↔</span> {t("guideDrag")}
                </li>
                <li>
                  <span aria-hidden="true">‹ ›</span> {t("guideArrows")}
                </li>
                <li>
                  <span aria-hidden="true">▣</span> {t("guideSides")}
                </li>
                <li>
                  <span aria-hidden="true">⌕</span> {t("guideZoom")}
                </li>
              </ul>
              <button type="button" className="bce-guide-ok" onClick={dismissGuide}>
                {t("guideOk")}
              </button>
            </div>
          </>
        )}
      </div>

      <p className="bce-rotate-hint">
        {zoomed ? (
          t("zoomPanHint")
        ) : (
          <>
            ↶ {t("rotateHint")} <strong>360°</strong> ↷
          </>
        )}
        {!showGuide && (
          <button type="button" className="bce-guide-reopen" onClick={() => setShowGuide(true)}>
            {t("showGuide")}
          </button>
        )}
      </p>

      <div className="bce-view-controls" role="group" aria-label={t("chooseSide")}>
        {VIEWS.map((view) => (
          <button
            key={view}
            type="button"
            className={activeView === view ? "is-selected" : ""}
            aria-pressed={activeView === view}
            onClick={() => {
              track("bag_view_change", { ...bagParams(collection), view });
              goTo(view);
            }}
          >
            {VIEW_LABELS[view][lang]}
          </button>
        ))}
      </div>

      <div className="bce-scale-controls">
        {collection.hotspots.length > 0 && (
          <button type="button" className="bce-hotspot-toggle" aria-pressed={showHotspots} onClick={toggleHotspots}>
            {showHotspots ? t("hideHotspots") : t("showHotspots")}
          </button>
        )}
      </div>
    </div>
  );
}
